/**
 * Renders a journalist author avatar.
 * - Facebook share/reel URLs → embedded Facebook iframe (only when size="hero")
 * - Any other URL → <img>
 * - When URL is a Facebook link and size is "sm" or "md", falls back to initials
 *   (a 40x40 FB iframe wouldn't look right in a byline)
 */
export function AuthorAvatar({ name = "", src = "", size = "sm", className = "" }) {
  const isFacebook = typeof src === "string" && src.includes("facebook.com");
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const dims = {
    sm: "w-8 h-8 text-xs",
    md: "w-12 h-12 text-sm",
    lg: "w-24 h-24 text-2xl",
    hero: "",
  }[size];

  if (size === "hero" && isFacebook) {
    return (
      <div className={`w-full aspect-[3/4] border overflow-hidden ${className}`} style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="author-avatar-fb">
        <div className="w-full h-full bg-gradient-to-br from-[#1877F2] to-[#0a4aa0] text-white flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-white/15 flex items-center justify-center mb-4">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </div>
          <div className="kicker opacity-80 mb-2">On Facebook</div>
          <div className="font-serif text-2xl font-bold mb-4">{name}</div>
          <a href={src} target="_blank" rel="noreferrer" className="bg-white text-[#1877F2] font-bold uppercase text-xs tracking-widest px-4 py-2 hover:bg-white/90" data-testid="author-avatar-fb-link">Open Photo</a>
        </div>
      </div>
    );
  }

  if (size === "hero" && src) {
    return (
      <img
        src={src}
        alt={name}
        className={`w-full aspect-[3/4] object-cover object-top grayscale hover:grayscale-0 transition-all duration-700 ${className}`}
        data-testid="author-avatar"
      />
    );
  }

  // Compact avatars — never render an iframe here, fall back to initials
  if (isFacebook || !src) {
    return (
      <div
        className={`rounded-full bg-[color:var(--text)] text-[color:var(--bg)] flex items-center justify-center font-bold ${dims} ${className}`}
        aria-label={name}
        data-testid="author-avatar-initials"
      >
        {initials || "J"}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={`rounded-full object-cover object-top ${dims} ${className}`}
      data-testid="author-avatar"
    />
  );
}

function fbEmbed(url) {
  return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=false&width=500`;
}

// Kept in case a future public-embeddable URL is provided; not actively used.
export { fbEmbed };
